import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/dcrLeaveType.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const BroadcastChannel = () => {
  const navigate = useNavigate();
  const [divisions, setDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [selectedDivisions, setSelectedDivisions] = useState([]); // currently stores division.name
  const [selectedStates, setSelectedStates] = useState([]); // stores state.id (number)
  const [selectedDesignations, setSelectedDesignations] = useState([]); // stores designation.id (number)
  const [selectedEmployees, setSelectedEmployees] = useState([]);

  const [message, setMessage] = useState("");
  const [startDate, setStartDate] = useState(
    () => new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(() => {
    const next = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    return next.toISOString().split("T")[0];
  });

  const [divisionCounts, setDivisionCounts] = useState({});
  const [stateCounts, setStateCounts] = useState({});
  const [designationCounts, setDesignationCounts] = useState({});

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // load divisions, states, designations, employees in parallel
        const [divRes, stateRes, desigRes, empRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() }),
          axios.get(`${BASE_URL}/api/designations/`, {
            headers: getAuthHeaders(),
          }),
          axios.get(`${BASE_URL}/api/employees`, { headers: getAuthHeaders() }), // ensure this endpoint exists
        ]);

        setDivisions(Array.isArray(divRes.data) ? divRes.data : []);
        setStates(Array.isArray(stateRes.data) ? stateRes.data : []);
        setDesignations(Array.isArray(desigRes.data) ? desigRes.data : []);
        setEmployees(Array.isArray(empRes.data) ? empRes.data : []);

        const divCount = {};
        const stateCount = {};
        const desigCount = {};

        (empRes.data || []).forEach((emp) => {
          // adapt these keys depending on your employee payload
          const divKey = emp.division ?? emp.division_name ?? emp.divisionId;
          const stateKey = emp.state_id ?? emp.stateId;
          const desigKey =
            emp.designation ?? emp.designation_name ?? emp.designationId;

          if (divKey !== undefined)
            divCount[divKey] = (divCount[divKey] || 0) + 1;
          if (stateKey !== undefined)
            stateCount[stateKey] = (stateCount[stateKey] || 0) + 1;
          if (desigKey !== undefined)
            desigCount[desigKey] = (desigCount[desigKey] || 0) + 1;
        });

        setDivisionCounts(divCount);
        setStateCounts(stateCount);
        setDesignationCounts(desigCount);
      } catch (err) {
        console.error("Error fetching data:", err);
      }
    };

    fetchInitialData();
  }, []);

  // Derived filtered employees if you use it later
  const filteredEmployees = employees.filter(
    (emp) =>
      (selectedDivisions.length === 0 ||
        selectedDivisions.includes(emp.division)) &&
      (selectedStates.length === 0 || selectedStates.includes(emp.state_id)) &&
      (selectedDesignations.length === 0 ||
        selectedDesignations.includes(emp.designation_id || emp.designation))
  );

  const handleCheckboxChange = (value, selectedList, setList) => {
    setList((prev) =>
      prev.includes(value) ? prev.filter((i) => i !== value) : [...prev, value]
    );
  };

  const handleSelectAll = (items, selectedList, setList, key) => {
    if (!Array.isArray(items) || items.length === 0) {
      setList([]);
      return;
    }

    if (selectedList.length === items.length) {
      setList([]);
    } else {
      const values = items.map((item) => {
        if (key === "division") return item.name; // we keep divisions as names in selection
        if (key === "state") return item.id;
        if (key === "designation") return item.id;
        if (key === "employee") return item.id;
        return item.id;
      });
      setList(values);
    }
  };

  useEffect(() => {
    if (selectedDivisions.length === 0) setSelectedStates([]);
  }, [selectedDivisions]);

  useEffect(() => {
    if (selectedStates.length === 0) setSelectedDesignations([]);
  }, [selectedStates]);

  useEffect(() => {
    if (selectedDesignations.length === 0) setSelectedEmployees([]);
  }, [selectedDesignations]);

  // Build payload and POST to /api/broadcasts
  const handleSubmit = async () => {
    // validation
    if (!message?.trim()) {
      alert("Please enter a message");
      return;
    }
    if (!startDate || !endDate) {
      alert("Please select start and end dates");
      return;
    }
    // At least one target (division/state/designation/employee) should be selected
    const anySelected =
      selectedDivisions.length ||
      selectedStates.length ||
      selectedDesignations.length ||
      selectedEmployees.length;
    if (!anySelected) {
      alert(
        "Please select at least one division, state, designation or employee"
      );
      return;
    }

    // Map selectedDivisions (which we stored as names) back to division ids
    const divisionIds = (selectedDivisions || [])
      .map((name) => {
        const found = divisions.find(
          (d) => d.name === name || String(d.id) === String(name)
        );
        return found ? Number(found.id) : null;
      })
      .filter(Boolean);

    const stateIds = (selectedStates || [])
      .map((s) => Number(s))
      .filter((n) => !Number.isNaN(n));

    const designationIds = (selectedDesignations || [])
      .map((d) => Number(d))
      .filter((n) => !Number.isNaN(n));

    // If selectedEmployees are used, ensure they're numbers
    const employeeIds = (selectedEmployees || [])
      .map((e) => Number(e))
      .filter((n) => !Number.isNaN(n));

    // Build final payload (only include arrays even if empty, as your example showed arrays)
    const payload = {
      divisions: divisionIds,
      states: stateIds,
      designations: designationIds,
      message: message,
      start_date: startDate,
      end_date: endDate,
    };

    try {
      const resp = await axios.post(`${BASE_URL}/api/broadcasts`, payload, {
        headers: getAuthHeaders(),
      });
      // success feedback
      alert("Broadcast created successfully");
      // reset form but do not change styling or page layout
      setMessage("");
      setSelectedDivisions([]);
      setSelectedStates([]);
      setSelectedDesignations([]);
      setSelectedEmployees([]);
      // reset dates to defaults
      setStartDate(new Date().toISOString().split("T")[0]);
      setEndDate(
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
          .toISOString()
          .split("T")[0]
      );
      // If you want to navigate or reload list, do it here
      // navigate('/dashboard') // optional
    } catch (err) {
      console.error(
        "Broadcast create failed:",
        err.response?.data ?? err.message
      );
      alert("Failed to create broadcast. Check console for details.");
    }
  };

  return (
    <div className="dcr-leave-type-main-container">
      <div className="dcr-leave-type-page-container">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
          <span className="text-blue-700">Add </span>
          <span className="text-blue-500">Broadcast </span>
        </h1>

        <div className="dcr-leave-type-filter-container">
          <div className="dcr-leave-type-division-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Division</span>
              <input
                type="checkbox"
                checked={
                  selectedDivisions.length === divisions.length &&
                  divisions.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    divisions,
                    selectedDivisions,
                    setSelectedDivisions,
                    "division"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-division-list">
              {divisions.map((d) => (
                <div key={d.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedDivisions.includes(d.name)}
                    onChange={() =>
                      handleCheckboxChange(
                        d.name,
                        selectedDivisions,
                        setSelectedDivisions
                      )
                    }
                  />
                  {d.name} ({divisionCounts[d.name] || 0})
                </div>
              ))}
            </div>
          </div>

          <div className="dcr-leave-type-state-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>State</span>
              <input
                type="checkbox"
                checked={
                  selectedStates.length === states.length && states.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    states,
                    selectedStates,
                    setSelectedStates,
                    "state"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-state-list">
              {states.map((s) => (
                <div key={s.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedStates.includes(s.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        s.id,
                        selectedStates,
                        setSelectedStates
                      )
                    }
                  />
                  {s.name} ({stateCounts[s.id] || 0})
                </div>
              ))}
            </div>
          </div>

          <div className="dcr-leave-type-designation-section">
            <label className="dcr-leave-type-label-checkbox">
              <span>Designation</span>
              <input
                type="checkbox"
                checked={
                  selectedDesignations.length === designations.length &&
                  designations.length > 0
                }
                onChange={() =>
                  handleSelectAll(
                    designations,
                    selectedDesignations,
                    setSelectedDesignations,
                    "designation"
                  )
                }
              />
            </label>
            <div className="dcr-leave-type-designation-list">
              {designations.map((d) => (
                <div key={d.id} className="dcr-leave-type-checkbox-item">
                  <input
                    type="checkbox"
                    checked={selectedDesignations.includes(d.id)}
                    onChange={() =>
                      handleCheckboxChange(
                        d.id,
                        selectedDesignations,
                        setSelectedDesignations
                      )
                    }
                  />
                  {d.name} ({designationCounts[d.name] || 0})
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="dcr-leave-type-form-row">
          <div className="dcr-leave-type-form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="dcr-leave-type-form-group">
            <label style={{ color: "red" }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="dcr-leave-type-form-group">
            <label>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows="3"
            />
          </div>
        </div>

        <div className="dcr-leave-type-button-row">
          <button className="dcr-leave-type-submit-btn" onClick={handleSubmit}>
            Submit
          </button>
          <button
            onClick={() => navigate(`/dashboard`)}
            className="dcr-leave-type-cancel-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default BroadcastChannel;
